/**
 * useSearchData — 검색결과 종합/탭 공용 데이터 훅.
 *   현행 MultiSearchResults 의 섹션 fetch 로직을 추출해, "전체" 탭(스택)과
 *   개별 탭(관세율표·분류사례·관세사해외·주해설·이미지)이 동일 데이터를 공유하고
 *   탭 바 카운트를 얻는다.
 *
 *   ⚠ 관세율표는 keyword 우선 — hybrid 상시 호출은 시맨틱 스캔 비용 때문에 피한다
 *     (keyword 0.13s). 다만 keyword 가 화면 0건(items+heading_matches 모두 없음)일 때만
 *     hybrid 를 1회 재시도한다 — LeftAiPanel mode=auto 캐스케이드와 동일 의미론.
 *     hybrid 재시도가 실패(500 등)하면 keyword 의 빈 결과를 그대로 유지(무회귀).
 *   섹션별 독립 병렬 — 느린 섹션이 다른 섹션을 막지 않음.
 */
import { useEffect, useMemo, useState } from "react";
import { splitPrecedentsBySource } from "./multiSearchData";
import { userAuthHeaders } from "./userAuth";

export function fetchJson(endpoint, params, signal) {
  const u = new URL(endpoint, window.location.origin);
  for (const [k, v] of Object.entries(params)) {
    if (v != null && v !== "") u.searchParams.set(k, v);
  }
  const headers = endpoint === "/api/onto/tariff-search" ? userAuthHeaders() : {};
  return fetch(u.toString(), { signal, headers }).then((r) =>
    r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)),
  );
}

// 섹션 fetch 공통 훅 — { loading, items, error } + retry
export function useSection(q, run) {
  const [state, setState] = useState({
    loading: true,
    items: [],
    matchTokens: [],
    error: false,
  });
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    if (!q) return undefined;
    const ctrl = new AbortController();
    setState({ loading: true, items: [], matchTokens: [], error: false });
    run(ctrl.signal)
      .then((result) => {
        const envelope = Array.isArray(result) ? { items: result } : (result || {});
        setState({
          loading: false,
          items: Array.isArray(envelope.items) ? envelope.items : [],
          matchTokens: Array.isArray(envelope.matchTokens) ? envelope.matchTokens : [],
          error: false,
        });
      })
      .catch((e) => {
        if (e && e.name === "AbortError") return;
        setState({ loading: false, items: [], matchTokens: [], error: true });
      });
    return () => ctrl.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, retry]);
  return [state, () => setRetry((n) => n + 1)];
}

// image_atch_ids 는 매퍼가 array_to_string(…,',') 로 문자열일 수 있음(또는 배열).
function normAtchIds(v) {
  const arr = Array.isArray(v) ? v : typeof v === "string" ? v.split(",") : [];
  return arr.map((s) => String(s).trim()).filter(Boolean);
}

// 이미지 결과도 원본 분류사례의 식별자와 출처를 유지해야 상세 화면으로
// 이동한 뒤 동일한 검색 컨텍스트로 돌아올 수 있다.
export function buildSearchImages(cases, rulings) {
  return [...(cases || []), ...(rulings || [])]
    .map((c) => ({
      case_id: c.case_id,
      source: c.source,
      hsk: c.hsk || c.hs6 || "",
      label: c.label || "",
      description: [c.desc, c.reasoning].filter(Boolean).join("\n"),
      match_snippet: c.match_snippet || "",
      atch_ids: normAtchIds(c.image_atch_ids),
    }))
    .filter((c) => c.case_id && c.atch_ids.length);
}

export function useSearchData(q, tariffData) {
  // ① 관세율표 — props(tariffData) 재사용, 없으면 자체 keyword fetch 폴백
  const [tariffFallback, setTariffFallback] = useState(null);
  useEffect(() => {
    if (tariffData || !q) {
      setTariffFallback(null);
      return undefined;
    }
    const ctrl = new AbortController();
    const toState = (d, mode) => ({
      q,
      items: (d && d.items) || [],
      headingMatches: (d && d.heading_matches) || [],
      matchTokens: Array.isArray(d && d.match_tokens) ? d.match_tokens : [],
      fallback: (d && d.fallback) || null,
      nameMatches: Array.isArray(d && d.name_matches) ? d.name_matches : [],   // 서버 명칭 일치(동의어 경유), 2026-09-15
      needsRefine: !!(d && d.needs_refine),
      mode: (d && d.mode) || mode,
    });
    fetchJson("/api/onto/tariff-search", { q, mode: "keyword", limit: 30 }, ctrl.signal)
      .then(async (d) => {
        const kw = toState(d, "keyword");
        if (kw.items.length || kw.headingMatches.length) return kw;
        // 화면 0건일 때만 의미검색 폴백 — 실패하면 keyword 빈 결과 유지
        try {
          const h = await fetchJson(
            "/api/onto/tariff-search", { q, mode: "hybrid", limit: 30 }, ctrl.signal,
          );
          const hy = toState(h, "hybrid");
          return hy.items.length || hy.headingMatches.length ? hy : kw;
        } catch (e) {
          if (e && e.name === "AbortError") throw e;
          return kw;
        }
      })
      .then((next) => setTariffFallback(next))
      .catch((e) => {
        if (e && e.name === "AbortError") return;
        setTariffFallback({
          q,
          items: [],
          headingMatches: [],
          matchTokens: [],
          fallback: null,
          needsRefine: false,
          mode: "keyword",
        });
      });
    return () => ctrl.abort();
  }, [q, tariffData]);
  const tariff = tariffData || tariffFallback;

  // ②+⑤ 공유(precedents-search 1회) / ③ 주해설 / ④ 법령 — 독립 병렬
  const [prec, retryPrec] = useSection(q, (s) =>
    fetchJson("/api/onto/precedents-search", { q, mode: "keyword", limit: 20 }, s).then(
      (d) => ({
        items: (d && d.items) || [],
        matchTokens: (d && (d.match_tokens || d.refined_tokens)) || [],
      }),
    ),
  );
  const [notes, retryNotes] = useSection(q, (s) =>
    fetchJson("/api/onto/notes-search", { q, mode: "keyword", limit: 10 }, s).then(
      (d) => ({
        items: (d && d.items) || [],
        matchTokens: (d && (d.match_tokens || d.refined_tokens)) || [],
      }),
    ),
  );
  const [laws, retryLaws] = useSection(q, (s) =>
    fetchJson("/api/onto/laws-search", { q, mode: "keyword", limit: 10 }, s).then(
      (d) => ({
        items: (d && d.items) || [],
        matchTokens: (d && (d.match_tokens || d.refined_tokens)) || [],
      }),
    ),
  );

  const { cases, rulings } = useMemo(() => splitPrecedentsBySource(prec.items), [prec.items]);

  // 이미지 탭 — image_atch_ids 있는 사례만
  const images = useMemo(
    () => buildSearchImages(cases, rulings),
    [cases, rulings],
  );

  const tariffCount = tariff ? (tariff.items || []).length + (tariff.headingMatches || []).length : null;

  return {
    tariff,
    precState: prec,
    cases,
    rulings,
    notesState: notes,
    lawsState: laws,
    retry: { prec: retryPrec, notes: retryNotes, laws: retryLaws },
    images,
    counts: {
      tariff: tariffCount,
      cases: prec.loading ? null : cases.length,
      foreign: prec.loading ? null : rulings.length,
      notes: notes.loading ? null : notes.items.length,
      laws: laws.loading ? null : laws.items.length,
      images: prec.loading ? null : images.length,
    },
  };
}
